import { Identifiable } from '@approvalmax/types';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { ItemForEdit } from 'pages/requestForm/components';
import { useCallback, useMemo, useState } from 'react';
import { mapMatchingInfo } from 'shared/components';
import { useLazyGetLineItemMatchingSuggestions } from 'shared/data/webApp/v2';

import {
    changeQbooksLineItemsAllChecked,
    changeQbooksLineItemsClass,
    changeQbooksLineItemsCustomer,
    changeQbooksLineItemsItem,
    changeQbooksLineItemsTaxCode,
    cloneQbooksLineItems,
    cloneQbooksLineItemsAll,
    removeQbooksLineItems,
    removeQbooksLineItemsAll,
} from '../../../actions';
import { getQBooksContext } from '../../../selectors/qbooks';
import type { ExpandedQBooksLineItem } from '../../../selectors/types/ExpandedQBooksLineItem';
import { QBooksLineItemCustomerCell } from '../QBooksLineItemCustomerCell/QBooksLineItemCustomerCell';
import { QbooksLineItemDropdownCell } from '../QbooksLineItemDropdownCell/QbooksLineItemDropdownCell';
import { QbooksLineItemItemCell } from '../QBooksLineItemItemCell/QbooksLineItemItemCell';
import { messages } from './QBooksLineItemsSection.messages';
import type { UseGroupEditItemsParams } from './QBooksLineItemsSection.types';

export const useGroupEditItems = ({
    lineItems,
    taxCodesList,
    itemVisible,
    taxVisible,
    classVisible,
    customerVisible,
}: UseGroupEditItemsParams) => {
    const dispatch = useDispatch();

    const [isGroupEditPopupVisible, setIsGroupEditPopupVisible] = useState(false);
    const selectedItems = useMemo(() => lineItems.filter((el) => el.checked), [lineItems]);
    const qbooksContext = useSelector(getQBooksContext);

    const handleGroupRemove = useCallback(() => {
        if (selectedItems.length === lineItems.length) {
            dispatch(removeQbooksLineItemsAll());
        } else {
            dispatch(removeQbooksLineItems(selectedItems.map((lineItem) => lineItem.id)));
        }
    }, [dispatch, lineItems.length, selectedItems]);

    const handleGroupClone = useCallback(() => {
        if (selectedItems.length === lineItems.length) {
            dispatch(cloneQbooksLineItemsAll());
        } else {
            dispatch(cloneQbooksLineItems(selectedItems.map((item) => item.id)));
        }

        dispatch(changeQbooksLineItemsAllChecked(false));
    }, [dispatch, lineItems.length, selectedItems]);

    const openGroupEdit = useCallback(() => {
        setIsGroupEditPopupVisible(true);
    }, []);

    const closeGroupEdit = useCallback(() => {
        setIsGroupEditPopupVisible(false);
        dispatch(changeQbooksLineItemsAllChecked(false));
    }, [dispatch]);

    const groupItems = useMemo(() => {
        const res: ItemForEdit<Identifiable>[] = [];

        if (itemVisible) {
            res.push({
                id: 'item',
                name: messages.colNameItem,
                render: ({ value, onChange }) => (
                    <QbooksLineItemItemCell
                        isSalesInvoice={false}
                        value={value as domain.QBooksItem}
                        onChange={onChange}
                        placeholder={messages.cellPlaceholderItem}
                    />
                ),
                onSave: (newItem: domain.QBooksItem) => {
                    dispatch(
                        changeQbooksLineItemsItem(
                            selectedItems.map((lineItem) => lineItem.id),
                            newItem
                        )
                    );
                },
            });
        }

        if (taxVisible) {
            res.push({
                id: 'taxRate',
                name: messages.colNameTaxCode,
                onSave: (newTax: domain.QBooksTaxCode) => {
                    dispatch(
                        changeQbooksLineItemsTaxCode(
                            selectedItems.map((lineItem) => lineItem.id),
                            newTax
                        )
                    );
                },
                render: ({ value, onChange }) => {
                    return (
                        <QbooksLineItemDropdownCell
                            items={taxCodesList}
                            value={value}
                            onChange={onChange}
                            placeholder={messages.cellPlaceholderTaxCode}
                        />
                    );
                },
            });
        }

        if (classVisible) {
            res.push({
                id: 'classes',
                name: messages.colNameClass,
                onSave: (newTax: domain.QBooksTaxCode) => {
                    dispatch(
                        changeQbooksLineItemsClass(
                            selectedItems.map((lineItem) => lineItem.id),
                            newTax
                        )
                    );
                },
                render: ({ value, onChange }) => {
                    return (
                        <QbooksLineItemDropdownCell
                            items={qbooksContext.classes}
                            value={value}
                            onChange={onChange}
                            placeholder={messages.cellPlaceholderClass}
                        />
                    );
                },
            });
        }

        if (customerVisible) {
            res.push({
                id: 'customer',
                name: messages.colNameCustomer,
                onSave: (newTax: domain.QBooksTaxCode) => {
                    dispatch(
                        changeQbooksLineItemsCustomer(
                            selectedItems.map((lineItem) => lineItem.id),
                            newTax
                        )
                    );
                },
                render: ({ value, onChange }) => {
                    return (
                        <QBooksLineItemCustomerCell
                            value={value}
                            onChange={onChange}
                            placeholder={messages.cellPlaceholderCustomer}
                        />
                    );
                },
            });
        }

        return res;
    }, [
        classVisible,
        customerVisible,
        dispatch,
        itemVisible,
        qbooksContext.classes,
        selectedItems,
        taxCodesList,
        taxVisible,
    ]);

    const isGroupRemoveVisible = useMemo(() => selectedItems.every((item) => !item.hideRemove), [selectedItems]);
    const isGroupCloneVisible = useMemo(() => selectedItems.every((item) => !item.hideClone), [selectedItems]);
    const isGroupEditVisible = groupItems.length > 0;
    const isShowGroupButtons =
        selectedItems.length > 0 && (isGroupRemoveVisible || isGroupCloneVisible || isGroupEditVisible);

    return {
        isShowGroupButtons,
        handleGroupRemove,
        handleGroupClone,
        openGroupEdit,
        closeGroupEdit,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isGroupCloneVisible,
        isGroupEditPopupVisible,
        groupItems,
    };
};

export const useConstructLineItem = (
    request:
        | selectors.types.QBooksBillExpandedRequest
        | selectors.types.QBooksPoExpandedRequest
        | selectors.types.QBooksExpenseExpandedRequest
        | selectors.types.QBooksInvoiceExpandedRequest,
    lineItems: ExpandedQBooksLineItem[]
) => {
    const [triggerGetLineItemMatchingSuggestions] = useLazyGetLineItemMatchingSuggestions();

    const vendorId = 'vendor' in request.details ? request.details.vendor?.id : undefined;

    const constructLineItem = useCallback(
        async (lineItemId: string) => {
            const lineItem = lineItems.find((lineItem) => lineItem.id === lineItemId);
            const data = await triggerGetLineItemMatchingSuggestions({
                params: {
                    path: { companyId: request.companyId },
                    query: {
                        requestId: request.id,
                        vendorId: vendorId || '',
                        billDate: request.details.date,
                        itemId: lineItem?.item?.id || '',
                    },
                },
            });

            const purchaseOrder = data.purchaseOrders?.find((purchaseOrder) =>
                purchaseOrder.itemBasedLines?.some(
                    (itemBasedLine) => itemBasedLine.lineItemId === lineItem?.matchedLineItemId
                )
            );

            if (!purchaseOrder) {
                return;
            }

            const { accountBasedLines, itemBasedLines, ...rest } = purchaseOrder;

            return mapMatchingInfo({
                ...rest,
                // TODO: BE don't have time to support correct types between v2 and v1, and we don't have time
                //  to wait, so as a temporary solution we casting types
                //  @see https://approvalmax.atlassian.net/browse/AM-20926
                itemBasedLine: itemBasedLines?.find(
                    (itemBasedLine) => itemBasedLine.lineItemId === lineItem?.matchedLineItemId
                ) as Parameters<typeof mapMatchingInfo>[0]['itemBasedLine'],
            });
        },
        [
            triggerGetLineItemMatchingSuggestions,
            lineItems,
            request.companyId,
            request.details.date,
            request.id,
            vendorId,
        ]
    );

    return { constructLineItem };
};
